define(
    "diy/undo-redo/History",
    [
        'jquery',
        'diy/undo-redo/Observable',
        'diy/undo-redo/constants'
    ],
    /**
     *
     * @param {jquery} $
     * @param {module.undoRedo.Observable} Observable
     * @param {module.undoRedo.constants} constants
     * @returns {*}
     */
    function ($,
              Observable,
              constants) {
        'use strict';
        var History;

        /**
         * @class module.undoRedo.History
         * @template module.undoRedo.History
         * @lends {module.undoRedo.History.prototype}
         * @extends {module.undoRedo.Observable}
         * @constructor
         */
        History = Observable.extend({

            status: constants.status.INIT,

            /** @type {module.undoRedo.Api} */
            api: null,

            /** @type {module.undoRedo.SessionManager} */
            sessionManager: null,

            /** @type {Array.<{topic: String, callback: Function}>} */
            observers: [],

            /** @type {module.undoRedo.Storage} */
            storage: null,

            /** @type {Number} */
            localEntriesLimit: 0,

            /** @type {module.undoRedo.EntryFactory} */
            entryFactory: null,

            /** @type {Boolean} */
            chainsLocked: false,

            /** @type {module.undoRedo.Queue} */
            whenReady: null,

            /** @type {module.undoRedo.RemoteRequest} */
            backend: null,

            /** @type {{undo:[], redo:[], last:{}}} */
            chains: undefined,

            /**
             *
             * @param {module.undoRedo.EntryFactory} entryFactory
             * @param {module.undoRedo.Queue} readyQueue
             * @param {module.undoRedo.RemoteRequest} backend
             * @param {module.undoRedo.SessionManager} sessionManager
             * @param {Number=} localEntriesLimit
             */
            initialize: function initialize(entryFactory, readyQueue, backend, sessionManager, localEntriesLimit) {
                this.entryFactory = entryFactory;
                this.whenReady = readyQueue;
                this.localEntriesLimit = localEntriesLimit;
                this.backend = backend;
                this.sessionManager = sessionManager;
                this.observers = [];

                this.resetChains();
                this.markStatus(constants.status.INIT);
            },

            /**
             *
             * @returns {Promise|Q.promise}
             */
            init: function init() {

                // init readyQueue settings
                this.whenReady
                    .setAutoStart(true)
                    .init(
                        function /** callWhenLocked */ () {
                            this.markStatus(constants.status.LOCK);
                        }.bind(this),
                        function /** callWhenUnLocked */ () {

                        }.bind(this),
                        function /** callWhenNothingToRun */ () {
                            this.markStatus(constants.status.READY);
                        }.bind(this)
                    );

                return this.refresh();
            },
            /**
             *
             * @returns {Promise|Q.promise}
             */
            refresh: function refresh() {
                return this.populateChains()
                    .then(function () {
                        this.markStatus(constants.status.READY);
                    }.bind(this));
            },
            /**
             *
             * @param {Object=} injectedServerHistory
             */
            populateChains: function populateChains(injectedServerHistory) {
                var sequence = [];

                sequence.push(function () {
                    this.resetChains();
                    return this.storage.loadDatabase(injectedServerHistory);
                }.bind(this));

                /**
                 * loads raw data into chains entries
                 * @see {module.undoRedo.Entry}
                 */
                sequence.push(
                    /**
                     * @param {Object} loadedData
                     * @param {Array.<Object>} loadedData.undoList
                     * @param {Array.<Object>} loadedData.redoList
                     * @param {Object} loadedData.lastEntry
                     * @this {module.undoRedo.History}
                     */
                    function (loadedData) {
                        this.addEntriesToChain(loadedData.undoList, this.chains.undo);
                        this.addEntriesToChain(loadedData.redoList, this.chains.redo);
                        this.addLastEntry(loadedData.lastEntry);
                    }.bind(this)
                );

                /**
                 * marks corresponding entries to session heads
                 */
                sequence.push(
                    this.markSessionHeads.bind(this)
                );

                /**
                 * loads data and runs the populate chain sequence
                 */
                return sequence.reduce(Q.when, this.markStatus(constants.status.LOADING));
            },

            markSessionHeads: function markSessionHeads() {
                this.setSessionHeads(this.chains.undo);
                this.setSessionHeads(this.chains.redo);
            },
            /**
             *
             * @param {Array.<module.undoRedo.Entry>} onChain
             * @private
             */
            setSessionHeads: function setSessionHeads(onChain) {
                onChain.forEach(function (currentEntry, sourceIndex, sourceChain) {
                    if (currentEntry.getExecutionType() === constants.executionType.SERVER) {
                        sourceChain[sourceIndex].setIsSessionHead(false);

                        this.setLinkedEntries(currentEntry, sourceIndex, sourceChain, this.chains.undo);
                        this.setLinkedEntries(currentEntry, sourceIndex, sourceChain, this.chains.redo);
                    }
                }.bind(this));
            },
            /**
             *
             * @param {module.undoRedo.Entry} currentEntry
             * @param {Number} sourceIndex
             * @param {Array.<module.undoRedo.Entry>} sourceChain
             * @param {Array.<module.undoRedo.Entry>} targetChain
             * @private
             */
            setLinkedEntries: function setLinkedEntries(currentEntry, sourceIndex, sourceChain, targetChain) {
                targetChain.forEach(function (loopEntry, loopIndex, loop) {
                    if (loopEntry.getExecutionType() === constants.executionType.CLIENT &&
                        loopEntry.getUndoSessionId() === currentEntry.getUndoSessionId() &&
                        loopEntry.getSessionIndex() === 1
                    ) {
                        sourceChain[sourceIndex].setIsSessionHead(true);
                        loop[loopIndex].setLinkedEntryId(currentEntry.getEntryId());
                    }
                });
            },

            addEntriesToChain: function addEntriesToChain(list, chain) {
                list.forEach(function (payload) {
                    chain.push(this.entryFactory.load(payload.manager, payload));
                }.bind(this));
            },

            addLastEntry: function addLastEntry(payload) {
                if (typeof payload !== 'undefined' && payload !== null) {
                    this.chains.last = this.entryFactory.build(payload.manager, payload);
                }
            },
            /**
             *
             * @param {module.undoRedo.Entry} entry
             * @returns {Promise|Q.promise}
             */
            addEntry: function addEntry(entry) {

                this.chains.undo.unshift(entry);

                return this.storage.addUndoEntry(entry.toDatabase(), entry.getKey())
                    .then(this.deleteRedoChain.bind(this, false))
                    .then(this.markSessionHeads.bind(this))
                    .then(function(){
                        return Q(entry.getKey());
                    });
            },
            /**
             * Clones an entry to the opposite chain
             *  - original entry is unlinked from it's chain
             *
             * @param {module.undoRedo.Entry} entryToBeSwitched
             * @param {Object} payload
             * @param {Number=} newIndex
             * @param {String=} newIndex
             * @param {String=} newMessage
             */
            switchChains: function(entryToBeSwitched, payload, newIndex, newMessage) {
                var updatedEntry;

                if (payload === null) {
                    updatedEntry = $.extend(true, {}, entryToBeSwitched);
                    if (typeof newIndex !== 'undefined') {
                        updatedEntry.setSessionIndex(newIndex);
                    }
                    if (typeof newMessage !== 'undefined') {
                        updatedEntry.setMessage(newMessage);
                    }
                } else {
                    updatedEntry = this.entryFactory.build(
                        entryToBeSwitched.getManager(),
                        payload,
                        entryToBeSwitched.getMessage(),
                        entryToBeSwitched.getUndoSessionId(),
                        (typeof newIndex !== 'undefined') ? newIndex : entryToBeSwitched.getSessionIndex(),
                        entryToBeSwitched.isExecutable()
                    );

                    if (typeof newMessage !== 'undefined') {
                        updatedEntry.setMessage(newMessage);
                    }

                    updatedEntry
                        .setKey(entryToBeSwitched.getKey())
                        .setLinkedEntryId(entryToBeSwitched.getLinkedEntryId())
                        ;
                }

                switch (entryToBeSwitched.getType()) {
                    case constants.types.UNDO:
                        updatedEntry.setType(constants.types.REDO);
                        this.chains.undo = this.chains.undo.filter(function(item){
                            return item.getKey() !== updatedEntry.getKey();
                        });

                        this.chains.redo.unshift(updatedEntry);
                    break;

                    case constants.types.REDO:
                        updatedEntry.setType(constants.types.UNDO);
                        this.chains.redo = this.chains.redo.filter(function(item){
                            return item.getKey() !== updatedEntry.getKey();
                        });

                        this.chains.undo.unshift(updatedEntry);
                    break;
                }
                this.sessionManager.markInSession(updatedEntry);

                return this.storage.updateEntry(updatedEntry.getKey(), updatedEntry.toDatabase());
            },
            /**
             *
             * @returns {module.undoRedo.History}
             */
            resetChains: function resetChains() {
                this.chains = {undo: [], redo: [], last: null};
                return this;
            },

            /**
             *
             * @param {module.undoRedo.Storage} storage
             */
            setStorage: function setStorage(storage) {
                this.storage = storage;
            },
            /**
             *
             * @param {Number} status
             * @returns {module.undoRedo.History}
             */
            markStatus: function markStatus(status) {
                switch (status) {
                    case constants.status.READY:
                        if(this.status !== status) {
                            this.status = status;
                            this.notifyObservers(constants.observerTopics.HISTORY_CHAIN_READY, this.chains);
                        } else {
                            this.status = status;
                        }
                        break;
                    case constants.status.SYNC:
                        this.status = constants.status.READY;
                        break;
                    default:
                        this.status = status;
                        break;
                }
                this.notifyObservers(constants.observerTopics.HISTORY_CHAIN_UPDATED, this.chains);
                return this;
            },

            /**
             *
             * @param {Function} callback
             * @param {Boolean=} autoLock [default=true]
             */
            executeWhenReady: function executeWhenReady(callback, autoLock) {
                autoLock = (typeof autoLock === 'undefined') ? true : autoLock;
                this.whenReady.add(callback, autoLock);
            },
            /**
             *
             * @param {Boolean=} ignoreServer
             * @returns {Promise|Q.promise}
             */
            deleteRedoChain: function deleteRedoChain(ignoreServer) {

                return this.storage.deleteRedoEntries()
                    .then(function() {
                        if (ignoreServer === true){
                            return Q(false);
                        } else {
                            return this.deleteServerRedoChain();
                        }
                    }.bind(this))
                    .then(function(wasDeletionOnServerSide){
                        this.chains.redo = [];
                        return Q(wasDeletionOnServerSide);
                    }.bind(this));
            },

            deleteServerRedoChain: function deleteServerRedoChain() {
                var serverEntriesInChain;

                serverEntriesInChain = this.chains.redo.some(function(element) {//, index, array) {
                    return (element.getExecutionType() === constants.executionType.SERVER);
                });

                if (serverEntriesInChain) {
                    return this.backend.deleteRedoChain();
                } else {
                    return Q(false);
                }
            },
            /**
             *
             * @param {String} undoSessionId
             * @returns {Promise|Q.Promise}
             */
            deleteUndoSession: function deleteUndoSession(undoSessionId) {
                var deferred = Q.defer();

                if (this.status === constants.status.INIT) {
                    return this.executeDeleteUndoSession(undoSessionId);
                } else {
                    this.executeWhenReady(function () {
                        this.executeDeleteUndoSession(undoSessionId)
                            .then(function () {
                                deferred.resolve(true);
                            })
                            .catch(function (error) {
                                deferred.reject(error);
                            });
                        return deferred.promise;
                    }.bind(this));
                }
                return deferred.promise;
            },
            /**
             *
             * @param undoSessionId
             * @returns {Promise|Q.Promise}
             */
            executeDeleteUndoSession: function executeDeleteUndoSession (undoSessionId) {
                var deletedKeys = [];
                this.deleteUndoSessionFromChain(constants.types.UNDO, undoSessionId, deletedKeys);
                this.deleteUndoSessionFromChain(constants.types.REDO, undoSessionId, deletedKeys);

                return this.storage.deleteEntriesFromSession(undoSessionId)
                    .then(this.markSessionHeads.bind(this));
            },
            /**
             *
             * @param {Number} chainType
             * @param {String} undoSessionId
             * @param {Array.<String>} deletedEntryKeys
             */
            deleteUndoSessionFromChain: function deleteUndoSessionFromChain(chainType, undoSessionId, deletedEntryKeys) {
                var index = 0, chain, listEntry;

                chain = ((chainType === constants.types.UNDO)? this.chains.undo : this.chains.redo);

                while (index < chain.length) {
                    listEntry = chain[index];
                    if (listEntry.getUndoSessionId() === undoSessionId &&
                        listEntry.getExecutionType() === constants.executionType.CLIENT
                    ) {
                        // add entry key to be deleted later
                        deletedEntryKeys.push(listEntry.getKey());
                        // synchronously remove entry from chain, will avoid refresh
                        chain.splice(index, 1);
                    } else {
                        index++;
                    }
                }
            },

            /**
             *
             * @param {module.undoRedo.Entry[]} chain
             * @returns {module.undoRedo.Entry[]}
             */
            filterChainForUi: function filterChainForUi(chain) {
                return chain.filter(function (element /*, index, array */) {
                    return !(
                    element.getExecutionType() === constants.executionType.SERVER &&
                    element.getIsSessionHead() === true
                    );
                });
            },

            /**
             *
             * @param {String} key
             * @returns {module.undoRedo.Entry|null}
             */
            getEntryByKey: function getEntryByKey(key) {
                var entry;
                entry = this.getEntryByKeyInChain(key, this.chains.undo);
                if (entry === null){
                    entry = this.getEntryByKeyInChain(key, this.chains.redo);
                }
                return entry;
            },
            /**
             *
             * @param {String} key
             * @param {module.undoRedo.Entry[]} chain
             * @returns {module.undoRedo.Entry|null}
             */
            getEntryByKeyInChain: function(key, chain){
                var index;
                for (index = 0; index < chain.length; index++){
                    if (chain[index].getKey().toString() === key.toString()) {
                        return chain[index];
                    }
                }
                return null;
            }
        });

        return History;
    }
);



/*****************
 ** WEBPACK FOOTER
 ** ./source/cdn/document_root/s/diy/undo-redo/History.js
 ** module id = 906
 ** module chunks = 0
 **/